import dayjs from 'dayjs'
import React from 'react'
import { IoIosCloseCircleOutline, IoMdTime } from 'react-icons/io'
import { MdCalendarMonth } from 'react-icons/md'
import useSWR from 'swr'
import { BASE_URL, fetcher } from '../../utils/api'
import Spinner from '../Spinner'

const CampaignDetailModal = ({
  onClose,
  data,
  onEdit
}) => {
  const { data: campaign, isLoading, error, mutate } = useSWR(
    data?.id ? `campaigns/${data.id}` : null,
    fetcher
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white overflow-hidden dark:bg-dark rounded-lg shadow-lg w-full md:max-w-[800px] max-w-[90vw] relative max-h-[90vh] flex flex-col">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Campaign Details
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? <div className="py-4 grid items-center justify-center">
          <Spinner />
        </div> : !isLoading && campaign ? <>
          <div className="flex-1 overflow-y-auto p-6">
            {/* Top Info Row */}
            <div className='w-full flex flex-col lg:flex-row lg:items-center gap-x-6 gap-y-4'>
              {campaign?.scheduled_publish_date ? <>
                <div className='w-full'>
                  <div className='flex flex-row items-center gap-2'>
                    <MdCalendarMonth className='text-base text-gray-400' />
                    <p className='text-base text-gray-400'>Scheduled Date</p>
                  </div>
                  <p className='text-sm font-medium text-black dark:text-white'>
                    {dayjs(campaign?.scheduled_publish_date).format("ddd D MMM, YYYY")}
                  </p>
                </div>

                <div className='w-full'>
                  <div className='flex flex-row items-center gap-2'>
                    <IoMdTime className='text-base text-gray-400' />
                    <p className='text-base text-gray-400'>Scheduled Time</p>
                  </div>
                  <p className='text-sm font-medium text-black dark:text-white'>
                    {dayjs(campaign?.scheduled_publish_date).format("h:mm A")}
                  </p>
                </div>
              </> : <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <IoMdTime className='text-base text-gray-400' />
                  <p className='text-base text-gray-400'>Created on</p>
                </div>
                <p className='text-sm font-medium text-black dark:text-white'>
                      {`${dayjs(campaign?.created_at).format("ddd D MMM, YYYY")} ${dayjs(campaign?.created_at).format("h:mm A")}`}
                </p>
              </div>}


              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>Status</p>
                </div>
                <div className={`w-fit rounded-full ${campaign?.status === "delivered" || campaign?.status === "scheduled" ? "bg-gray-100 text-black/70" : campaign?.status === "failed" ? "bg-red-200 text-red-400" : "bg-gray-200"} text-sm capitalize font-medium py-1 px-3 flex flex-row items-center gap-2`}>
                  <div className={`w-2 h-2 rounded-full ${campaign?.status === "scheduled" ? "bg-gray-400" : campaign?.status === "failed" ? "bg-red-700" : "bg-green-700"}`} />
                  <p> {campaign?.status}</p>
                </div>
              </div>
            </div>

            {/* Campaign Info Grid */}
            <div className='my-4 border dark:border-border-dark px-2 py-5 w-full rounded-lg grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5'>
              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>
                    Campaign ID
                  </p>
                </div>
                <p className='text-sm font-medium text-black dark:text-white'>
                  {campaign?.campaign_id || "N/A"}
                </p>
              </div>

              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>
                    SMS Nickname
                  </p>
                </div>
                <p className='text-sm font-medium text-black dark:text-white'>
                  {campaign?.sms_nickname || campaign?.campaign_id || "N/A"}
                </p>
              </div>

              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>
                    Message Type
                  </p>
                </div>
                <p className='text-sm uppercase font-medium text-black dark:text-white'>
                  {campaign?.message_type || "N/A"}
                </p>
              </div>


              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>
                    Repeat
                  </p>
                </div>
                <p className='text-sm capitalize font-medium text-black dark:text-white'>
                  {campaign?.schedule_type || "N/A"}
                </p>
              </div>

              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>
                    Message Content
                  </p>
                </div>
                <p className='text-sm capitalize font-medium text-black dark:text-white'>
                  {campaign?.message_body || "N/A"}
                </p>
              </div>

              <div className='w-full'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>Title</p>
                </div>
                <p className='text-sm font-medium text-black dark:text-white'>
                  {campaign?.message_title || "N/A"}
                </p>
              </div>
              <div className='w-full md:col-span-2'>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-base text-gray-400'>Description</p>
                </div>
                <p className='text-sm font-medium text-black dark:text-white'>
                  {campaign?.description || "N/A"}
                </p>
              </div>
              {/* Audio File */}
              {campaign?.audio_file && (
                <div className='w-full md:col-span-3'>
                  <div className='flex flex-row items-center gap-2'>
                    <p className='text-base text-gray-400'>Voice File</p>
                  </div>
                  <audio controls src={campaign.audio_file} className='w-full mt-2'>
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>

            {/* Owner Info */}
              <div className='my-4 border  dark:border-border-dark px-2 py-5 w-full rounded-lg'>
                <h3 className='text-base font-semibold mb-2 text-gray-700 dark:text-white'>Owner</h3>
              <div className='flex flex-col md:flex-row gap-4'>
                <div>
                  <span className='text-gray-400 text-sm'>Name: </span>
                    <span className='font-medium text-sm dark:text-white'>{campaign?.owner?.firstname} {campaign?.owner?.lastname}</span>
                </div>
                <div>
                  <span className='text-gray-400 text-sm'>Email: </span>
                    <span className='font-medium text-sm dark:text-white'>{campaign?.owner?.email}</span>
                </div>
                <div>
                  <span className='text-gray-400 text-sm'>Phone: </span>
                    <span className='font-medium text-sm dark:text-white'>{campaign?.owner?.phone}</span>
                </div>
              </div>
            </div>

            {/* Recipients Info */}
              <div className='my-4 border  dark:border-border-dark px-2 py-5 w-full rounded-lg'>
                <h3 className='text-base font-semibold mb-2 text-gray-700 dark:text-white'>Recipients</h3>
              {campaign?.recipients?.length > 0 ? (
                <div className='flex flex-col gap-3'>
                  {campaign.recipients.map((group, idx) => (
                    <div key={group.id || idx} className='border  dark:border-border-dark rounded-md p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2'>
                      <div>
                        <span className='text-gray-400 text-sm'>Group Name: </span>
                        <span className='font-medium text-sm dark:text-white'>{group.group_name}</span>
                      </div>
                      <div>
                        <span className='text-gray-400 text-sm'>Type: </span>
                        <span className='font-medium capitalize text-sm dark:text-white'>{group.group_type}</span>
                      </div>
                      <div>
                        <span className='text-gray-400 text-sm'>Members: </span>
                        <span className='font-medium text-sm dark:text-white'>{group.total_members}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className='text-gray-400 text-sm'>No recipients</div>}
            </div>

            {/* Created/Updated At */}
              <div className='my-4 border  dark:border-border-dark px-2 py-5 w-full rounded-lg flex flex-row gap-8'>
              <div>
                <span className='text-gray-400 text-sm'>Created At: </span>
                  <span className='font-medium text-sm dark:text-white'>{campaign?.created_at ? dayjs(campaign.created_at).format("ddd D MMM, YYYY h:mm A") : "N/A"}</span>
              </div>
              <div>
                <span className='text-gray-400 text-sm'>Updated At: </span>
                  <span className='font-medium text-sm dark:text-white'>{campaign?.updated_at ? dayjs(campaign.updated_at).format("ddd D MMM, YYYY h:mm A") : "N/A"}</span>
              </div>
            </div>

          </div>

            <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
            <div className="flex flex-row items-end justify-end gap-4">
              <button
                onClick={onClose}
                  className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
                Close
              </button>

              {campaign?.status !== "published" && campaign?.status !== "sent" && campaign?.status !== "delivered" && <button
                onClick={onEdit}
                className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg`}>
                Edit Campaign
              </button>}
            </div>
          </div>

        </> : <></>}

      </div>
    </div>
  )
}

export default CampaignDetailModal